import React, { useCallback, useEffect, useState } from 'react'
import { Grid } from '@mui/material'
import { Button, Loader } from '@refera/ui-web'
import { navigate, useParams } from '@reach/router'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { createKanbanConfig, getKanbanConfig, updateKanbanConfig } from '_/modules/agency/actions'

import HeaderTitle from '_/components/header-title'
import { CustomizeCard } from './customize-card'
import { ColumnVisualization } from './column-visualization'

import { ROUTES } from '_/utils/constants'
import { useToast } from '_/hooks/use-toast'

import useStyles from './styles'
import { getHelpResources } from '_/modules/service-orders/actions'

// only to prevent errors when the config is not loaded yet
const DEFAULT_VALUES = {
  newOrders: null,
  negotiateBudget: null,
  cardFields: [],
}

export function KanbanConfig() {
  const styles = useStyles()
  const dispatch = useDispatch()

  const { agencyId } = useParams()

  const [kanbanConfig, setKanbanConfig] = useState(undefined)

  const [isLoading, setIsLoading] = useState(true)

  const { showToast } = useToast()

  const methods = useForm({ defaultValues: DEFAULT_VALUES })

  const { handleSubmit, setValue } = methods

  const handleBackNavigation = useCallback(() => {
    return navigate(`${ROUTES.AGENCY}/${agencyId}`)
  }, [navigate, agencyId])

  function handleDefaultValues(config) {
    const { cardFields = [], splitColumns = {} } = config?.params || {}

    setValue('newOrders', splitColumns?.newOrders || 'new_orders')
    setValue('negotiateBudget', splitColumns?.negotiateBudget || 'negotiate_budget')
    setValue('waitingBudget', splitColumns?.waitingBudget || 'waiting_budget')
    setValue('waitingExecution', splitColumns?.waitingExecution || 'waiting_execution')
    setValue('cardFields', Array.isArray(cardFields) ? cardFields : [])

    setKanbanConfig(config)
  }

  async function onSubmit(data) {
    const { newOrders, negotiateBudget, waitingBudget, waitingExecution, cardFields } = data

    setIsLoading(true)

    const payload = {
      splitColumns: {
        newOrders,
        negotiateBudget,
        waitingBudget,
        waitingExecution,
      },
      cardFields: Array.isArray(cardFields) ? cardFields : [],
    }

    const action = kanbanConfig?.agency ? updateKanbanConfig : createKanbanConfig
    await dispatch(action(agencyId, payload))
      .then(() => showToast({ type: 'success' }))
      .catch(() =>
        showToast({
          type: 'error',
        })
      )
      .finally(() => setIsLoading(false))
  }

  async function fetchHelpResources() {
    try {
      await dispatch(getHelpResources({}))
    } catch (err) {
      console.error('Erro ao buscar recursos de ajuda', err)
      throw err
    }
  }

  async function fetchKanbanColumns() {
    try {
      const res = await dispatch(getKanbanConfig(agencyId))
      handleDefaultValues(res)
    } catch (err) {
      showToast({
        message: 'Erro ao buscar configurações do Kanban',
        type: 'error',
      })
      throw err
    }
  }

  async function setupPage() {
    setIsLoading(true)
    try {
      const settle = p =>
        p
          .then(value => ({ status: 'fulfilled', value }))
          .catch(reason => ({ status: 'rejected', reason }))

      // Substitui .allSettled para não ter que usar o polyfill
      await Promise.all([settle(fetchKanbanColumns()), settle(fetchHelpResources())])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setupPage()
  }, [])

  return (
    <Grid>
      <Loader open={isLoading} hasBackdrop />
      <HeaderTitle title="Configurações do Kanban" backButtonAction={handleBackNavigation}>
        <Grid className={styles.buttonsContainer}>
          <Button color="red" variant="secondary" onClick={handleBackNavigation}>
            Cancelar
          </Button>
          <Button
            type="submit"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoading || Object.keys(methods.errors).length > 0}
          >
            Salvar
          </Button>
        </Grid>
      </HeaderTitle>
      <Grid className={styles.container}>
        <FormProvider {...methods}>
          <ColumnVisualization />
          <CustomizeCard />
        </FormProvider>
      </Grid>
    </Grid>
  )
}
